import { Link } from "zudoku/components";
import { cn } from "zudoku";
import { CardIllustration, type IllustrationVariant } from "./CardIllustration.js";

const toneStyles: Record<
  IllustrationVariant,
  { panel: string; ring: string }
> = {
  quickstart: {
    panel: "bg-amber-50/80 dark:bg-amber-950/30",
    ring: "group-hover:ring-amber-200/80 dark:group-hover:ring-amber-800/50",
  },
  basics: {
    panel: "bg-rose-50/80 dark:bg-rose-950/25",
    ring: "group-hover:ring-rose-200/80 dark:group-hover:ring-rose-800/50",
  },
  reference: {
    panel: "bg-sky-50/80 dark:bg-sky-950/25",
    ring: "group-hover:ring-sky-200/80 dark:group-hover:ring-sky-800/50",
  },
  auth: {
    panel: "bg-emerald-50/80 dark:bg-emerald-950/25",
    ring: "group-hover:ring-emerald-200/80 dark:group-hover:ring-emerald-800/50",
  },
  journey: {
    panel: "bg-violet-50/80 dark:bg-violet-950/25",
    ring: "group-hover:ring-violet-200/80 dark:group-hover:ring-violet-800/50",
  },
  workflow: {
    panel: "bg-orange-50/80 dark:bg-orange-950/25",
    ring: "group-hover:ring-orange-200/80 dark:group-hover:ring-orange-800/50",
  },
  practices: {
    panel: "bg-teal-50/80 dark:bg-teal-950/25",
    ring: "group-hover:ring-teal-200/80 dark:group-hover:ring-teal-800/50",
  },
};

export type TopicCardProps = {
  title: string;
  description: string;
  href: string;
  variant: IllustrationVariant;
};

export function TopicCard({ title, description, href, variant }: TopicCardProps) {
  const tone = toneStyles[variant];

  return (
    <Link
      to={href}
      className={cn(
        "group not-prose flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card",
        "transition-all duration-200 hover:-translate-y-0.5 hover:border-border hover:shadow-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        tone.ring,
      )}
    >
      <div className={cn("aspect-[5/3] w-full", tone.panel)}>
        <CardIllustration variant={variant} />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 px-5 py-4">
        <h3 className="text-base font-semibold tracking-tight text-foreground">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}
